import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { PageHeader, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FileText, Download, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";

const VISIBILITY_OPTIONS = [
    { value: "COMMUNITY", label: "Community — all residents" },
    { value: "FLAT", label: "Specific flat(s)" },
    { value: "RESIDENT", label: "Specific resident(s)" },
    { value: "PRIVATE", label: "Private — single resident" },
    { value: "COMMITTEE", label: "Committee + Admin only" },
    { value: "ADMIN", label: "Admin only" },
];

const HTML_INPUT_TYPE = { text: "text", number: "number", amount: "number", date: "date", datetime: "datetime-local", email: "email", phone: "tel", time: "time" };

export default function DocumentGeneration() {
    const [templates, setTemplates] = useState([]);
    const [flats, setFlats] = useState([]);
    const [members, setMembers] = useState([]);
    const [residentOptions, setResidentOptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedFlat, setSelectedFlat] = useState(null);
    const [selectedIssuer, setSelectedIssuer] = useState(null);
    const [selectedAuthorizer, setSelectedAuthorizer] = useState(null);
    const [fieldValues, setFieldValues] = useState({});
    const [sendEmail, setSendEmail] = useState(false);
    const [visibilityType, setVisibilityType] = useState("COMMUNITY");
    const [selectedFlats, setSelectedFlats] = useState([]);
    const [selectedResidentIds, setSelectedResidentIds] = useState([]);
    const [signatureText, setSignatureText] = useState("");
    const [availablePeriods, setAvailablePeriods] = useState([]);

    useEffect(() => {
        loadData();
        generatePeriodOptions();
    }, []);

    function generatePeriodOptions() {
        const periods = [];
        const now = new Date();
        for (let i = 0; i < 12; i++) {
            const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const monthStr = date.toISOString().slice(0, 7);
            const displayStr = date.toLocaleString('default', { month: 'long', year: 'numeric' });
            periods.push({ value: monthStr, label: displayStr });
        }
        setAvailablePeriods(periods);
    }

    async function loadData() {
        setLoading(true);
        try {
            const [templatesRes, flatsRes, usersRes] = await Promise.all([
                api.get("/document-templates"),
                api.get("/flats"),
                api.get("/users")
            ]);
            setTemplates((templatesRes.data || []).filter(t => (t.status || "active") !== "archived"));
            setFlats(flatsRes.data);

            const authorizedMembers = usersRes.data.filter(m => m.role === 'admin' || m.role === 'committee');
            const enrichedMembers = authorizedMembers.map(member => {
                if (member.flat_id) {
                    const flat = flatsRes.data.find(f => f.id === member.flat_id);
                    return { ...member, flat };
                }
                return member;
            });
            setMembers(enrichedMembers);
            setResidentOptions(usersRes.data.filter(u => u.role === 'resident'));
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    }

    function handleTemplateChange(templateId) {
        const template = templates.find(t => t.id === templateId);
        setSelectedTemplate(template);

        const initialValues = {};
        (template?.fields || []).forEach(field => {
            initialValues[field.name] = "";
        });
        setFieldValues(initialValues);
        setSelectedFlat(null);
    }

    async function handleFlatSelection(flatId) {
        const flat = flats.find(f => f.id === flatId);
        setSelectedFlat(flat);
        if (!flat) return;

        const autoValues = { ...fieldValues };
        const flatDisplayNumber = `${flat.block}-${flat.number}`;
        if (selectedTemplate?.fields?.some(f => f.name === 'flat_number')) {
            autoValues.flat_number = flatDisplayNumber;
        }
        if (selectedTemplate?.fields?.some(f => f.name === 'resident_name')) {
            autoValues.resident_name = flat.owner_name || '';
        }
        if (selectedTemplate?.fields?.some(f => f.name === 'date')) {
            autoValues.date = new Date().toISOString().split('T')[0];
        }
        setFieldValues(autoValues);
        if (autoValues.period) {
            await fetchInvoiceAmount(flatId, autoValues.period);
        }
    }

    async function fetchInvoiceAmount(flatId, period) {
        if (!flatId || !period) return;
        try {
            const response = await api.get(`/invoices?flat_id=${flatId}&month=${period}`);
            const totalAmount = response.data.reduce((sum, inv) => sum + (inv.amount_due || inv.amount || 0), 0);
            setFieldValues(prev => ({ ...prev, amount: totalAmount > 0 ? totalAmount.toString() : prev.amount }));
            if (totalAmount > 0) toast.success(`Amount for ${period}: ₹${totalAmount}`);
        } catch (e) {
            // best-effort auto-fill only
        }
    }

    async function handlePeriodRangeChange(fromMonth, toMonth) {
        if (!fromMonth || !toMonth) return;
        const flatId = selectedFlat?.id;
        if (!flatId) {
            toast.error("Please select a flat first");
            return;
        }
        let start = fromMonth, end = toMonth;
        if (new Date(toMonth + "-01") < new Date(fromMonth + "-01")) {
            [start, end] = [toMonth, fromMonth];
        }
        const fromLabel = new Date(start + "-01").toLocaleString('default', { month: 'long', year: 'numeric' });
        const toLabel = new Date(end + "-01").toLocaleString('default', { month: 'long', year: 'numeric' });
        const periodDisplay = start === end ? fromLabel : `${fromLabel} to ${toLabel}`;
        setFieldValues(prev => ({ ...prev, period: periodDisplay }));

        try {
            const months = [];
            let current = new Date(start + "-01");
            const endDate = new Date(end + "-01");
            while (current <= endDate) {
                months.push(current.toISOString().slice(0, 7));
                current.setMonth(current.getMonth() + 1);
            }
            let totalAmount = 0;
            for (const month of months) {
                const response = await api.get(`/invoices?flat_id=${flatId}&month=${month}&status=unpaid`);
                totalAmount += response.data.reduce((sum, inv) => sum + (inv.amount_due || inv.amount || 0), 0);
            }
            setFieldValues(prev => ({ ...prev, amount: totalAmount > 0 ? totalAmount.toString() : prev.amount }));
            if (totalAmount > 0) toast.success(`Total for ${months.length} month(s): ₹${totalAmount.toLocaleString()}`);
        } catch (e) {
            toast.error("Failed to fetch invoice data");
        }
    }

    function handleFieldChange(fieldName, value) {
        setFieldValues(prev => ({ ...prev, [fieldName]: value }));
    }

    function handleIssuerSelection(memberId) {
        const member = members.find(m => m.id === memberId);
        setSelectedIssuer(member);
        if (!member) return;
        const autoValues = { ...fieldValues };
        if (selectedTemplate?.fields?.some(f => f.name === 'issued_by')) autoValues.issued_by = member.name || '';
        if (selectedTemplate?.fields?.some(f => f.name === 'designation')) {
            autoValues.designation = member.designation || (member.role?.charAt(0).toUpperCase() + member.role?.slice(1)) || '';
        }
        setFieldValues(autoValues);
        if (member.signature_url) setSignatureText(member.signature_url);
    }

    function handleAuthorizerSelection(memberId) {
        const member = members.find(m => m.id === memberId);
        setSelectedAuthorizer(member);
        if (!member) return;
        const autoValues = { ...fieldValues };
        if (selectedTemplate?.fields?.some(f => f.name === 'authorizer_name')) autoValues.authorizer_name = member.name || '';
        if (selectedTemplate?.fields?.some(f => f.name === 'authorizer_designation')) {
            autoValues.authorizer_designation = member.designation || (member.role?.charAt(0).toUpperCase() + member.role?.slice(1)) || '';
        }
        setFieldValues(autoValues);
    }

    function toggleFlatSelection(flatId) {
        setSelectedFlats(prev => prev.includes(flatId) ? prev.filter(id => id !== flatId) : [...prev, flatId]);
    }

    function toggleResidentSelection(residentId) {
        setSelectedResidentIds(prev => prev.includes(residentId) ? prev.filter(id => id !== residentId) : [...prev, residentId]);
    }

    function handleVisibilityChange(v) {
        setVisibilityType(v);
        setSelectedFlats([]);
        setSelectedResidentIds([]);
    }

    async function generateDocument() {
        if (!selectedTemplate) {
            toast.error("Please select a template");
            return;
        }
        const emptyFields = (selectedTemplate.fields || []).filter(f => f.required !== false && !fieldValues[f.name]?.trim?.());
        if (emptyFields.length > 0) {
            toast.error(`Please fill in all fields: ${emptyFields.map(f => f.label).join(", ")}`);
            return;
        }
        if (visibilityType === "FLAT" && selectedFlats.length === 0) {
            toast.error("Select at least one flat for Flat visibility");
            return;
        }
        if ((visibilityType === "RESIDENT" || visibilityType === "PRIVATE") && selectedResidentIds.length === 0) {
            toast.error("Select at least one resident");
            return;
        }

        setGenerating(true);
        try {
            const payload = {
                template_id: selectedTemplate.id,
                field_values: fieldValues,
                send_email: sendEmail,
                visibility_type: visibilityType,
                recipient_flats: visibilityType === "FLAT" ? selectedFlats : null,
                resident_ids: (visibilityType === "RESIDENT" || visibilityType === "PRIVATE") ? selectedResidentIds : null,
                digital_signature: signatureText || null
            };

            await api.post("/documents/generate", payload);
            toast.success(sendEmail ? "Document generated and emails sent!" : "Document generated successfully!");

            setSelectedTemplate(null);
            setFieldValues({});
            setSendEmail(false);
            setVisibilityType("COMMUNITY");
            setSelectedFlats([]);
            setSelectedResidentIds([]);
            setSignatureText("");
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setGenerating(false);
        }
    }

    if (loading) return <LoadingState />;

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Documents"
                title="Generate Document"
                description="Create documents from templates with custom data"
            />

            <div className="bg-white border border-brand-line rounded-sm p-6 max-w-3xl">
                <div className="space-y-6">
                    <div className="space-y-2">
                        <Label>Select Template *</Label>
                        <Select value={selectedTemplate?.id || ""} onValueChange={handleTemplateChange}>
                            <SelectTrigger data-testid="generate-template-select">
                                <SelectValue placeholder="Choose a document template..." />
                            </SelectTrigger>
                            <SelectContent>
                                {templates.map(template => (
                                    <SelectItem key={template.id} value={template.id}>
                                        {template.name} ({template.category})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {selectedTemplate && (
                        <>
                            <div className="border-t border-brand-line pt-6 space-y-4">
                                <div className="space-y-2">
                                    <Label>Select Flat to Auto-Fill Details (Optional)</Label>
                                    <Select value={selectedFlat?.id || ""} onValueChange={handleFlatSelection}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Choose a flat to auto-populate resident details..." />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {flats.map(flat => (
                                                <SelectItem key={flat.id} value={flat.id}>
                                                    {flat.block}-{flat.number} {flat.owner_name ? `- ${flat.owner_name}` : "- Vacant"}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="border-t border-brand-line pt-6 space-y-4">
                                <h3 className="font-heading font-semibold text-brand-ink">Fill Document Fields</h3>

                                {selectedTemplate.fields?.map((field) => (
                                    <div key={field.name} className="space-y-2">
                                        <Label>
                                            {field.label} {field.required !== false && "*"}
                                            <span className="text-xs text-brand-inkSoft ml-2">({field.type})</span>
                                        </Label>

                                        {field.name === 'issued_by' ? (
                                            <div className="space-y-2">
                                                <Select value={selectedIssuer?.id || ""} onValueChange={handleIssuerSelection}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select member to issue document..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {members.map(member => (
                                                            <SelectItem key={member.id} value={member.id}>
                                                                {member.name} - {member.role?.charAt(0).toUpperCase() + member.role?.slice(1)}
                                                                {member.flat?.block && member.flat?.number ? ` (${member.flat.block}-${member.flat.number})` : ''}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        ) : field.name === 'authorizer_name' ? (
                                            <div className="space-y-2">
                                                <Select value={selectedAuthorizer?.id || ""} onValueChange={handleAuthorizerSelection}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select authorizer..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {members.map(member => (
                                                            <SelectItem key={member.id} value={member.id}>
                                                                {member.name} - {member.role?.charAt(0).toUpperCase() + member.role?.slice(1)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        ) : field.name === 'period' ? (
                                            <div className="space-y-3">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <Label className="text-xs">From Month</Label>
                                                        <Select
                                                            value={fieldValues.period_from || ""}
                                                            onValueChange={(value) => {
                                                                setFieldValues(prev => ({ ...prev, period_from: value }));
                                                                if (fieldValues.period_to) handlePeriodRangeChange(value, fieldValues.period_to);
                                                            }}
                                                        >
                                                            <SelectTrigger><SelectValue placeholder="Start month..." /></SelectTrigger>
                                                            <SelectContent>
                                                                {availablePeriods.map(p => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs">To Month</Label>
                                                        <Select
                                                            value={fieldValues.period_to || ""}
                                                            onValueChange={(value) => {
                                                                setFieldValues(prev => ({ ...prev, period_to: value }));
                                                                if (fieldValues.period_from) handlePeriodRangeChange(fieldValues.period_from, value);
                                                            }}
                                                        >
                                                            <SelectTrigger><SelectValue placeholder="End month..." /></SelectTrigger>
                                                            <SelectContent>
                                                                {availablePeriods.map(p => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                                {fieldValues.period && <p className="text-xs text-brand-ink font-medium">Selected: {fieldValues.period}</p>}
                                            </div>
                                        ) : field.type === "textarea" ? (
                                            <Textarea
                                                value={fieldValues[field.name] || ""}
                                                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                placeholder={`Enter ${field.label.toLowerCase()}...`}
                                                rows={4}
                                            />
                                        ) : field.type === "resident" ? (
                                            <Select value={fieldValues[field.name] || ""} onValueChange={(v) => handleFieldChange(field.name, v)}>
                                                <SelectTrigger><SelectValue placeholder="Select resident..." /></SelectTrigger>
                                                <SelectContent>
                                                    {residentOptions.map(r => (<SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>))}
                                                </SelectContent>
                                            </Select>
                                        ) : field.type === "flat" || field.type === "block" ? (
                                            <Select value={fieldValues[field.name] || ""} onValueChange={(v) => handleFieldChange(field.name, v)}>
                                                <SelectTrigger><SelectValue placeholder="Select flat..." /></SelectTrigger>
                                                <SelectContent>
                                                    {flats.map(fl => (<SelectItem key={fl.id} value={fl.id}>{fl.block}-{fl.number}</SelectItem>))}
                                                </SelectContent>
                                            </Select>
                                        ) : field.type === "dropdown" && field.options?.length ? (
                                            <Select value={fieldValues[field.name] || ""} onValueChange={(v) => handleFieldChange(field.name, v)}>
                                                <SelectTrigger><SelectValue placeholder={`Select ${field.label.toLowerCase()}...`} /></SelectTrigger>
                                                <SelectContent>
                                                    {field.options.map(opt => (<SelectItem key={opt} value={opt}>{opt}</SelectItem>))}
                                                </SelectContent>
                                            </Select>
                                        ) : (
                                            <Input
                                                type={HTML_INPUT_TYPE[field.type] || "text"}
                                                value={fieldValues[field.name] || ""}
                                                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                placeholder={`Enter ${field.label.toLowerCase()}...`}
                                                disabled={(field.name === 'designation' && selectedIssuer) || (field.name === 'authorizer_designation' && selectedAuthorizer)}
                                                className={(field.name === 'designation' && selectedIssuer) || (field.name === 'authorizer_designation' && selectedAuthorizer) ? 'bg-gray-50' : ''}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Visibility */}
                            <div className="border-t border-brand-line pt-6 space-y-4">
                                <Label>Document Visibility *</Label>
                                <Select value={visibilityType} onValueChange={handleVisibilityChange}>
                                    <SelectTrigger data-testid="visibility-type-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {VISIBILITY_OPTIONS.map(o => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                                    </SelectContent>
                                </Select>

                                {visibilityType === "FLAT" && (
                                    <div className="border border-brand-line rounded-sm p-4 max-h-56 overflow-y-auto space-y-2" data-testid="visibility-flat-list">
                                        {flats.map(flat => (
                                            <div key={flat.id} className="flex items-center space-x-2">
                                                <Checkbox checked={selectedFlats.includes(flat.id)} onCheckedChange={() => toggleFlatSelection(flat.id)} />
                                                <label className="text-sm">{flat.block}-{flat.number} {flat.owner_name ? `- ${flat.owner_name}` : ""}</label>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {(visibilityType === "RESIDENT" || visibilityType === "PRIVATE") && (
                                    <div className="border border-brand-line rounded-sm p-4 max-h-56 overflow-y-auto space-y-2" data-testid="visibility-resident-list">
                                        {residentOptions.map(r => (
                                            <div key={r.id} className="flex items-center space-x-2">
                                                <Checkbox checked={selectedResidentIds.includes(r.id)} onCheckedChange={() => toggleResidentSelection(r.id)} />
                                                <label className="text-sm">{r.name}</label>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="flex items-center space-x-2 pt-2">
                                    <Checkbox id="send-email" checked={sendEmail} onCheckedChange={setSendEmail} />
                                    <label htmlFor="send-email" className="text-sm font-medium">
                                        Send email notification to eligible recipients
                                    </label>
                                </div>
                            </div>

                            <div className="border-t border-brand-line pt-6 space-y-4">
                                <Label>Digital Signature (Optional)</Label>
                                <Input
                                    value={signatureText}
                                    onChange={(e) => setSignatureText(e.target.value)}
                                    placeholder="Enter your name to add digital signature"
                                />
                                <p className="text-xs text-brand-inkSoft">
                                    Visual signature only — your name and role will be added at the bottom of the document.
                                    This is not a cryptographic e-signature.
                                </p>
                            </div>

                            <div className="flex justify-end pt-4">
                                <Button
                                    onClick={generateDocument}
                                    disabled={generating}
                                    className="bg-brand-action hover:bg-brand-actionHover text-white rounded-full"
                                    data-testid="generate-document-button"
                                >
                                    {generating ? "Generating..." : (
                                        sendEmail ? (
                                            <><PaperPlaneTilt size={16} className="mr-2" />Generate & Send</>
                                        ) : (
                                            <><Download size={16} className="mr-2" />Generate Document</>
                                        )
                                    )}
                                </Button>
                            </div>
                        </>
                    )}

                    {!selectedTemplate && (
                        <div className="text-center py-12">
                            <FileText size={48} className="mx-auto text-brand-inkSoft mb-4" />
                            <p className="text-brand-inkSoft">Select a template above to get started</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
